import { useEffect, useState } from 'react';
import { apiFetch } from '../api';
import SeasonalEffect from '../components/SeasonalEffect';

// Dashboard-Modul-Konfiguration: Welche Rolle sieht welches Modul
type DashboardModuleConfig = {
  requiredModules: string[]; // Modul muss gebucht sein
  allowedRoles?: string[];    // Optional: Nur diese Rollen dürfen sehen (wenn leer = alle)
  deniedRoles?: string[];     // Optional: Diese Rollen dürfen NICHT sehen
};

const DASHBOARD_CONFIG: Record<string, DashboardModuleConfig> = {
  dienstplan: {
    requiredModules: ['dienstplan'],
    allowedRoles: [], // Alle dürfen sehen wenn Modul aktiv
  },
  pos: {
    requiredModules: ['pos'],
    allowedRoles: [], // Alle dürfen sehen wenn Modul aktiv
  },
  poolwaesche: {
    requiredModules: ['poolwaesche'],
    allowedRoles: [],
  },
  bonuspunkte: {
    requiredModules: ['bonuspunkte'],
    allowedRoles: [],
  },
  guthaben: {
    requiredModules: ['balance'], // Nur wenn Balance-Modul gebucht
    allowedRoles: [], // Alle dürfen sehen
  },
  license_control: {
    requiredModules: ['license-control'],
    allowedRoles: [], // Alle dürfen sehen wenn Modul aktiv
  },
  storage: {
    requiredModules: ['storage'],
    allowedRoles: [], // Alle dürfen sehen wenn Modul aktiv
  },
  warenlager: {
    requiredModules: ['lager'],
    allowedRoles: [], // Alle dürfen sehen wenn Modul aktiv
  },
  vereinskasse: {
    requiredModules: ['vereinskasse'],
    allowedRoles: [], // Alle dürfen sehen wenn Modul aktiv
  },
  dms: {
    requiredModules: ['dms'],
    allowedRoles: [],
  },
};

type ComplianceStatus = {
  missing_documents: Array<{ id: number; name: string; description?: string }>;
  expired_documents: Array<{ id: number; template_name: string; expiry_date: string }>;
  expiring_documents: Array<{ id: number; template_name: string; expiry_date: string }>;
  missing_count: number;
  expired_count: number;
  expiring_count: number;
  license_expired: boolean;
  license_expiring_soon: boolean;
  has_issues: boolean;
};

type ModuleStats = {
  dienstplan?: {
    open_shifts: number;
    my_upcoming_shifts: number;
    pending_requests: number;
  };
  poolwaesche?: {
    total_items: number;
    issued_items: number;
    available_items: number;
  };
  pos?: {
    today_revenue: number;
    open_orders: number;
    payment_methods: Array<{ method: string; euros: number }>;
  };
  bonuspunkte?: {
    my_points: number;
    pending_redemptions: number;
  };
  guthaben?: {
    my_balance: number;
  };
  license_control?: {
    expiring_soon: number;
    total_licenses: number;
  };
  storage?: {
    total_items: number;
    low_stock: number;
    locations: number;
  };
  warenlager?: {
    total_articles: number;
    total_stock_value: number;
  };
  vereinskasse?: {
    total_cashboxes: number;
    current_balance_cents: number;
    open_entries: number;
  };
};

export default function OverviewDashboard() {
  const [stats, setStats] = useState<ModuleStats>({});
  const [compliance, setCompliance] = useState<ComplianceStatus | null>(null);
  const [dmsSummary, setDmsSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeModules, setActiveModules] = useState<string[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  useEffect(() => {
    loadUserRoles();
    loadCompliance();
  }, []);

  // Warte bis Rollen geladen sind, dann lade Module
  useEffect(() => {
    if (rolesLoaded) {
      loadModules();
    }
  }, [rolesLoaded]);

  async function loadDmsSummary() {
    try {
      const summary = await apiFetch('/dms/dashboard-summary');
      setDmsSummary(summary);
    } catch (e) {
      // DMS evtl. nicht verfügbar
    }
  }

  async function loadCompliance() {
    try {
      const data = await apiFetch('/profile/compliance-status').catch(() => null);
      if (data) setCompliance(data);
    } catch (e) {
      console.error('Compliance stats error:', e);
    }
  }

  function loadUserRoles() {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('vereinsos_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload?.roles || [];
        setUserRoles(roles);
        console.log('Dashboard: Loaded user roles:', roles);
      }
    } catch (e) {
      console.error('Dashboard: Failed to parse user roles:', e);
    } finally {
      setRolesLoaded(true);
    }
  }

  // Prüft ob ein Modul für den aktuellen Benutzer angezeigt werden darf
  function canShowModule(moduleKey: string, availableModules: string[]): boolean {
    const config = DASHBOARD_CONFIG[moduleKey];
    if (!config) {
      console.log(`canShowModule(${moduleKey}): No config found, returning false`);
      return false;
    }

    // 1. Prüfe ob benötigtes Modul gebucht ist
    if (config.requiredModules.length > 0) {
      const hasModule = config.requiredModules.some(m => availableModules.includes(m));
      if (!hasModule) return false;
    }

    // 2. Prüfe Rollen-Zugriff
    if (config.deniedRoles && config.deniedRoles.length > 0) {
      const isDenied = userRoles.some(role => config.deniedRoles!.includes(role));
      if (isDenied) return false;
    }

    if (config.allowedRoles && config.allowedRoles.length > 0) {
      const hasRole = userRoles.some(role => config.allowedRoles!.includes(role));
      if (!hasRole) return false;
    }

    return true;
  }

  async function loadModules() {
    try {
      const modules = await apiFetch('/admin/organization-modules').catch(() => []);
      
      if (modules.length > 0) {
        const active = modules
          .filter((m: any) => m.enabled)
          .map((m: any) => m.module_code);
        setActiveModules(active);
        if (active.includes('dms')) {
          loadDmsSummary();
        } else {
          setDmsSummary(null);
        }
        await loadStats(active);
      } else {
        const potentialModules = [
          'dienstplan', 'poolwaesche', 'pos', 'dashboard', 
          'bonuspunkte', 'lager', 'storage', 'warenlager', 'license-control'
        ];
        setActiveModules(potentialModules);
        await loadStats(potentialModules);
      }
    } catch (error) {
      console.error('Failed to load modules:', error);
      const allModules = [
        'dienstplan', 'poolwaesche', 'pos', 'dashboard', 
        'bonuspunkte', 'lager', 'storage', 'warenlager', 'license-control'
      ];
      setActiveModules(allModules);
      await loadStats(allModules);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats(modules: string[]) {
    const newStats: ModuleStats = {};

    // Dienstplan Stats
    if (canShowModule('dienstplan', modules) && (modules.includes('dienstplan') || modules.includes('dienstplaner'))) {
      try {
        const specialShifts = await apiFetch('/dienstplan/special-shifts').catch(() => []);
        const openSpecialShifts = specialShifts.filter((s: any) => 
          s.status === 'open' && !s.is_fully_staffed
        );
        
        const shifts = await apiFetch('/dienstplan/shifts').catch(() => []);
        const myShifts = shifts.filter((s: any) => s.assigned_to === getUserId());
        const now = new Date();
        const upcoming = myShifts.filter((s: any) => {
          const shiftDate = new Date(s.date + 'T' + s.start_time);
          return shiftDate > now;
        });
        
        newStats.dienstplan = {
          open_shifts: openSpecialShifts.length || 0,
          my_upcoming_shifts: upcoming.length || 0,
          pending_requests: 0
        };
      } catch (e) {
        console.error('Dienstplan stats error:', e);
      }
    }

    // Poolwäsche Stats
    if (canShowModule('poolwaesche', modules) && modules.includes('poolwaesche')) {
      try {
        const items = await apiFetch('/poolwaesche/items').catch(() => []);
        const issued = items.filter((i: any) => i.user_id).length;
        newStats.poolwaesche = {
          total_items: items.length || 0,
          issued_items: issued,
          available_items: (items.length - issued) || 0
        };
      } catch (e) {
        console.error('Poolwäsche stats error:', e);
      }
    }

    // Bonuspunkte Stats
    if (canShowModule('bonuspunkte', modules) && modules.includes('bonuspunkte')) {
      try {
        const points = await apiFetch('/bonuspunkte/my-points').catch(() => ({ total_points: 0 }));
        newStats.bonuspunkte = {
          my_points: points.total_points || 0,
          pending_redemptions: 0
        };
      } catch (e) {
        console.error('Bonuspunkte stats error:', e);
      }
    }

    // Guthaben Stats
    if (canShowModule('guthaben', modules)) {
      try {
        const userId = getUserId();
        if (userId) {
          const balance = await apiFetch(`/admin/balances/${userId}`).catch(() => ({ balance_cents: 0 }));
          newStats.guthaben = {
            my_balance: (balance.balance_cents || 0) / 100
          };
        }
      } catch (e) {
        console.error('Guthaben stats error:', e);
      }
    }

    // License Control Stats
    if (canShowModule('license_control', modules) && modules.includes('license-control')) {
      try {
        const dashboard = await apiFetch('/license-control/dashboard').catch(() => null);
        if (dashboard?.statistics) {
          const expiringSoon = dashboard.statistics.expiring_count || 0;
          const totalLicenses = dashboard.statistics.total_count || 0;
          
          newStats.license_control = {
            expiring_soon: expiringSoon,
            total_licenses: totalLicenses
          };
        }
      } catch (e) {
        console.error('License Control stats error:', e);
      }
    }

    // Storage/Lagerverwaltung Stats
    if (canShowModule('storage', modules) && (modules.includes('lager') || modules.includes('storage'))) {
      try {
        const items = await apiFetch('/admin/storage/items').catch(() => []);
        const locations = await apiFetch('/admin/storage/locations').catch(() => []);
        const lowStock = items.filter((item: any) => 
          item.min_quantity && item.current_quantity <= item.min_quantity
        ).length;
        
        newStats.storage = {
          total_items: items.length || 0,
          low_stock: lowStock,
          locations: locations.length || 0
        };
      } catch (e) {
        console.error('Storage stats error:', e);
      }
    }

    // Warenlager Stats
    if (canShowModule('warenlager', modules) && modules.includes('warenlager')) {
      try {
        const articles = await apiFetch('/warenlager/articles').catch(() => []);
        const totalValue = articles.reduce((sum: number, art: any) => 
          sum + (art.stock || 0) * (art.price_cents || 0) / 100, 0
        );
        
        newStats.warenlager = {
          total_articles: articles.length || 0,
          total_stock_value: totalValue
        };
      } catch (e) {
        console.error('Warenlager stats error:', e);
      }
    }

    // Vereinskasse Stats
    if (canShowModule('vereinskasse', modules) && modules.includes('vereinskasse')) {
      try {
        const cashboxes = await apiFetch('/cashbox').catch(() => []);
        
        const totalBalance = cashboxes.reduce((sum: number, cb: any) => {
          const balance = Number(cb.current_balance_cents) || 0;
          return sum + balance;
        }, 0);
        
        let openEntries = 0;
        for (const cb of cashboxes) {
          try {
            const entries = await apiFetch(`/cashbox/${cb.id}/entries?status=open`).catch(() => []);
            openEntries += entries.length;
          } catch {}
        }
        
        newStats.vereinskasse = {
          total_cashboxes: cashboxes.length || 0,
          current_balance_cents: totalBalance,
          open_entries: openEntries
        };
      } catch (e) {
        console.error('Vereinskasse stats error:', e);
      }
    }

    setStats(newStats);
  }

  function getUserId(): string | null {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('vereinsos_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.sub || payload?.userId || null;
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 p-4 sm:p-6 lg:p-8 font-sans space-y-8 relative">
      {/* Saisonaler Schneefall- / Emoji-Effekt vollflächig auf dem Dashboard */}
      <SeasonalEffect variant="dashboard" />
      <div className="max-w-7xl mx-auto space-y-8 relative z-10">
        
        {/* Top Header Card */}
        <div className="bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 dark:from-slate-900 dark:via-indigo-950/90 dark:to-purple-950/70 border border-indigo-500/40 dark:border-indigo-500/30 rounded-3xl shadow-xl dark:shadow-2xl p-6 sm:p-8 backdrop-blur-xl relative overflow-hidden space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/20 dark:border-indigo-500/20 pb-4">
            <div className="space-y-1">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 dark:bg-indigo-500/20 text-white dark:text-indigo-300 border border-white/30 dark:border-indigo-500/40 text-xs font-mono font-bold uppercase tracking-wider backdrop-blur-sm">
                <span>⚡ SYSTEM OVERVIEW</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">
                Dashboard &amp; Schnellzugriff
              </h1>
              <p className="text-sm text-indigo-100 dark:text-indigo-200/80 font-medium">
                Zentrale Übersicht über Schichten, Nachweise, Lagerbestände, Finanzen und Kennzahlen Ihrer Organisation.
              </p>
            </div>

            <div className="flex items-center gap-3 bg-black/20 dark:bg-slate-950/90 px-4 py-2.5 rounded-2xl border border-white/20 dark:border-indigo-800/50 shadow-inner shrink-0">
              <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse shadow-md shadow-emerald-500/50" />
              <div className="text-xs font-mono font-semibold text-slate-200">
                <span className="font-bold text-white">System Online</span>
                <span className="text-indigo-300 dark:text-indigo-400 block text-[10px]">OrgaPilot Portal 26.10.7</span>
              </div>
            </div>
          </div>
        </div>

        {/* 🚨 PERSÖNLICHE COMPLIANCE- / DOKUMENTE-WARNUNG */}
        {compliance && compliance.has_issues && (
          <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30 border-2 border-amber-300 dark:border-amber-800 rounded-2xl shadow-md flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-start gap-3.5">
              <span className="text-3xl shrink-0 p-2 bg-amber-100 dark:bg-amber-900/60 rounded-xl">⚠️</span>
              <div>
                <h3 className="text-base font-bold text-amber-950 dark:text-amber-200">
                  Wichtige Nachweise oder Dokumente ausstehend
                </h3>
                <p className="text-xs text-amber-800 dark:text-amber-300/90 mt-0.5 max-w-2xl">
                  {compliance.missing_count > 0 && `${compliance.missing_count} Pflichtdokument(e) fehlen. `}
                  {compliance.expired_count > 0 && `${compliance.expired_count} Nachweis(e) oder Führerschein abgelaufen. `}
                  Bitte überprüfe dein Profil und lade die erforderlichen Unterlagen hoch.
                </p>
              </div>
            </div>

            <a
              href="/profile"
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs rounded-xl shadow-sm transition-colors whitespace-nowrap shrink-0 flex items-center gap-1.5"
            >
              <span>📄 Zum Profil &amp; Nachweise</span>
              <span>→</span>
            </a>
          </div>
        )}

        {/* Dienstplan Section */}
        {stats.dienstplan && canShowModule('dienstplan', activeModules) && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-300 dark:border-gray-600 pb-2">
              <div className="w-10 h-10 rounded-2xl bg-blue-100 dark:bg-indigo-600/20 border border-blue-300 dark:border-indigo-500/40 flex items-center justify-center text-blue-700 dark:text-indigo-400 text-xl shadow-md">
                📅
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-whitetext-white tracking-tight">Dienstplan &amp; Schichten</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a href="/dienstplan/open" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-blue-600 dark:border-l-blue-500 hover:border-blue-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Offene Dienste</div>
                <div className="text-5xl font-black font-mono text-blue-600 dark:text-blue-400 group-hover:text-blue-700 dark:group-hover:text-blue-300 transition">{stats.dienstplan.open_shifts}</div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 group-hover:text-blue-800 dark:group-hover:text-white transition">Zum Dienstplan</span>
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>

              <a href="/dienstplan/kalender" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-indigo-600 dark:border-l-indigo-500 hover:border-indigo-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Meine kommenden Dienste</div>
                <div className="text-5xl font-black font-mono text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-700 dark:group-hover:text-indigo-300 transition">{stats.dienstplan.my_upcoming_shifts}</div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-800 dark:group-hover:text-white transition">Zum Kalender</span>
                  <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>

              <a href="/dienstplaner/inbox" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-purple-600 dark:border-l-purple-500 hover:border-purple-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Offene Anfragen</div>
                <div className="text-5xl font-black font-mono text-purple-600 dark:text-purple-400 group-hover:text-purple-700 dark:group-hover:text-purple-300 transition">{stats.dienstplan.pending_requests}</div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-purple-600 dark:text-purple-400 group-hover:text-purple-800 dark:group-hover:text-white transition">Zur Inbox</span>
                  <span className="text-xs font-bold text-purple-600 dark:text-purple-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>
            </div>
          </div>
        )}

        {/* Poolwäsche Section */}
        {stats.poolwaesche && canShowModule('poolwaesche', activeModules) && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-300 dark:border-gray-600 pb-2">
              <div className="w-10 h-10 rounded-2xl bg-cyan-100 dark:bg-cyan-600/20 border border-cyan-300 dark:border-cyan-500/40 flex items-center justify-center text-cyan-700 dark:text-cyan-400 text-xl shadow-md">
                👔
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-whitetext-white tracking-tight">Poolwäsche</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a href="/poolwaesche/inventory" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-cyan-600 dark:border-l-cyan-500 hover:border-cyan-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Gesamt Artikel</div>
                <div className="text-5xl font-black font-mono text-cyan-600 dark:text-cyan-400 group-hover:text-cyan-700 dark:group-hover:text-cyan-300 transition">{stats.poolwaesche.total_items}</div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 group-hover:text-cyan-800 dark:group-hover:text-white transition">Zum Inventar</span>
                  <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>

              <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-amber-600 dark:border-l-amber-500 p-6 rounded-2xl shadow-md dark:shadow-xl space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Ausgegeben</div>
                <div className="text-5xl font-black font-mono text-amber-600 dark:text-amber-400">{stats.poolwaesche.issued_items}</div>
                <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden border border-gray-300 dark:border-gray-600">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full" style={{ width: `${Math.min(100, (stats.poolwaesche.issued_items / (stats.poolwaesche.total_items || 1)) * 100)}%` }} />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-emerald-600 dark:border-l-emerald-500 p-6 rounded-2xl shadow-md dark:shadow-xl space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Verfügbar</div>
                <div className="text-5xl font-black font-mono text-emerald-600 dark:text-emerald-400">{stats.poolwaesche.available_items}</div>
                <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden border border-gray-300 dark:border-gray-600">
                  <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full" style={{ width: `${Math.min(100, (stats.poolwaesche.available_items / (stats.poolwaesche.total_items || 1)) * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        )}

      {/* DMS Widget */}
      {canShowModule('dms', activeModules) && dmsSummary && (dmsSummary.pending_count > 0 || dmsSummary.is_admin) && (
        <div style={{
          backgroundColor: '#1e293b',
          borderRadius: '12px',
          border: dmsSummary.pending_count > 0 ? '2px solid #ef4444' : '1px solid #334155',
          padding: '20px',
          marginBottom: '24px',
          color: '#f8fafc',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '2.5rem' }}>📑</div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#38bdf8' }}>
                Dokumenten-Management & Unterweisungen (DMS)
              </h3>
              <div style={{ fontSize: '0.9rem', color: '#cbd5e1', marginTop: '4px' }}>
                {dmsSummary.pending_count > 0 ? (
                  <span style={{ color: '#f87171', fontWeight: 600 }}>
                    ⚠️ Sie haben noch {dmsSummary.pending_count} offene Unterweisung(en) abzuzeichnen!
                  </span>
                ) : (
                  <span style={{ color: '#4ade80' }}>
                    ✅ Alle Ihnen zugewiesenen Unterweisungen sind aktuell abgezeichnet.
                  </span>
                )}
                {dmsSummary.is_admin && (
                  <span style={{ marginLeft: '12px', color: '#94a3b8' }}>
                    • Organisations-Quote: <strong>{dmsSummary.org_overall_compliance_rate}%</strong>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <a
              href="/dms"
              style={{
                backgroundColor: dmsSummary.pending_count > 0 ? '#dc2626' : '#0284c7',
                color: '#fff',
                padding: '9px 18px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.9rem'
              }}
            >
              {dmsSummary.pending_count > 0 ? '✍️ Jetzt abzeichnen' : '📂 Zum DMS'}
            </a>
            {dmsSummary.is_admin && (
              <a
                href="/dms/verwaltung"
                style={{
                  backgroundColor: '#334155',
                  color: '#fff',
                  padding: '9px 14px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '0.9rem'
                }}
              >
                ⚖️ Nachweise
              </a>
            )}
          </div>
        </div>
      )}

        {/* Bonuspunkte & Guthaben Section */}
        {(stats.bonuspunkte || stats.guthaben) && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.bonuspunkte && canShowModule('bonuspunkte', activeModules) && (
              <a href="/bonuspunkte" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-amber-600 dark:border-l-amber-500 hover:border-amber-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-100 dark:border-slate-800 pb-3">
                  <span className="text-amber-500 dark:text-amber-400 text-xl">⭐</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-whitetext-white uppercase tracking-wider">Bonuspunkte</span>
                </div>
                <div className="text-5xl font-black font-mono text-amber-600 dark:text-amber-400 group-hover:text-amber-700 dark:group-hover:text-amber-300 transition">{stats.bonuspunkte.my_points}</div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-amber-600 dark:text-amber-400 group-hover:text-amber-800 dark:group-hover:text-white transition">Punkte einlösen</span>
                  <span className="text-xs font-bold text-amber-600 dark:text-amber-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>
            )}

            {stats.guthaben && canShowModule('guthaben', activeModules) && (
              <a href="/profile" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-emerald-600 dark:border-l-emerald-500 hover:border-emerald-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="flex items-center gap-2 border-b border-gray-100 dark:border-slate-800 pb-3">
                  <span className="text-emerald-500 dark:text-emerald-400 text-xl">💳</span>
                  <span className="text-sm font-bold text-gray-900 dark:text-whitetext-white uppercase tracking-wider">Mein Guthaben</span>
                </div>
                <div className="text-5xl font-black font-mono text-emerald-600 dark:text-emerald-400 group-hover:text-emerald-700 dark:group-hover:text-emerald-300 transition">
                  {stats.guthaben.my_balance.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 group-hover:text-emerald-800 dark:group-hover:text-white transition">Zum Profil</span>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>
            )}
          </div>
        )}

        {/* Führerscheinkontrolle Section */}
        {stats.license_control && canShowModule('license_control', activeModules) && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 border-b border-gray-300 dark:border-gray-600 pb-2">
              <div className="w-10 h-10 rounded-2xl bg-rose-100 dark:bg-rose-600/20 border border-rose-300 dark:border-rose-500/40 flex items-center justify-center text-rose-700 dark:text-rose-400 text-xl shadow-md">
                🪪
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-whitetext-white tracking-tight">Führerscheinkontrolle</h2>
            </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <a href="/license-control" className="group bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-rose-600 dark:border-l-rose-500 hover:border-rose-500 p-6 rounded-2xl shadow-md dark:shadow-xl transition-all hover:-translate-y-1 space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Ablaufend in 30 Tagen</div>
                <div className="text-5xl font-black font-mono text-rose-600 dark:text-rose-400 group-hover:text-rose-700 dark:group-hover:text-rose-300 transition">{stats.license_control.expiring_soon}</div>
                <div className="pt-2 flex items-center justify-between border-t border-gray-100 dark:border-slate-800">
                  <span className="text-xs font-bold text-rose-600 dark:text-rose-400 group-hover:text-rose-800 dark:group-hover:text-white transition">Zur Kontrolle</span>
                  <span className="text-xs font-bold text-rose-600 dark:text-rose-400 group-hover:translate-x-1 transition">→</span>
                </div>
              </a>

              <div className="bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 border-l-4 border-l-gray-400 dark:border-l-slate-600 p-6 rounded-2xl shadow-md dark:shadow-xl space-y-4">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-400 uppercase tracking-wider">Erfasste Führerscheine</div>
                <div className="text-5xl font-black font-mono text-gray-900 dark:text-whitetext-white">{stats.license_control.total_licenses}</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
